import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { Small } from 'foundations-components/transitional/components/Small';
import * as TierGroup from 'marketplace-ui-common/constants/groupTags/TierGroups';
import * as Tier from 'marketplace-ui-common/constants/groupTags/Tiers';
import { getPricingUrl } from 'marketplace-ui-common/utils/urls';
import { Fragment, useCallback, useMemo, useState } from 'react';
import CompatibilityIcon from './CompatibilityIcon';
import { tierGroupNamesByProductType } from './constants';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function PlanCompatibilityMessage({
  appContext,
  compatibilityType,
  hasFeatureLevelScopeInfo = false,
  helpText,
  onCompatibilityModalOpen,
  onTrackInteraction = () => {},
  productType,
  tiers
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const pricingPageUrl = useMemo(() => {
    const lang = getLang();
    return getPricingUrl(appContext === 'public', '', lang);
  }, [appContext]);
  const tierGroupNames = useMemo(() => tierGroupNamesByProductType[productType] || [], [productType]);
  const tierGroupOrder = useMemo(() => tierGroupNames.map(tierGroupName => TierGroup[tierGroupName]), [tierGroupNames]);
  const validFlattenedTiers = useMemo(() => /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
  tierGroupOrder.reduce((final, tierGroup) => [...final, ...tierGroup], []), [tierGroupOrder]);
  const renderedTierGroups = useMemo(() => {
    if (tiers.includes(Tier.HUBSPOT_FREE)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.plans.allPlans"
      });
    }
    return tierGroupOrder.reduce((final, tierGroup) => {
      const includedTiers = tierGroup.filter(tier => tiers.includes(tier));
      if (!includedTiers.length) {
        return final;
      }
      const tier = includedTiers[0];
      const renderedTierLabel = /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: tier === includedTiers[includedTiers.length - 1] ? `marketplace-ui-common.groupTags.tier.${tier}` : 'detailsPage.compatibility.plans.lowestTier',
          options: {
            tier: unescapedText(`marketplace-ui-common.groupTags.tier.${tier}`)
          }
        }, tier)
      });
      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return final.concat(renderedTierLabel);
    }, []);
  }, [tiers, tierGroupOrder]);
  const sortTiers = useCallback((tierA, tierB) => validFlattenedTiers.indexOf(tierA) - validFlattenedTiers.indexOf(tierB), [validFlattenedTiers]);
  const sortedTierGroups = useMemo(() => {
    const tiersToSort = [...tiers];
    return tiersToSort.sort(sortTiers).reduce((groups, tier) => {
      const [tierHub, tierLevel] = tier.split('_');
      if (!validFlattenedTiers.includes(tier)) {
        return Object.assign({}, groups);
      } else if (groups[tierHub] === undefined) {
        return Object.assign({}, groups, {
          [tierHub]: [tierLevel]
        });
      } else {
        return Object.assign({}, groups, {
          [tierHub]: groups[tierHub].concat(tierLevel)
        });
      }
    }, {});
  }, [tiers, sortTiers, validFlattenedTiers]);
  const popoverContent = useMemo(() => {
    return {
      header: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.plans.compatibilityPopover.heading"
        })
      }),
      body: Object.keys(sortedTierGroups).map(tierHub => /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "200",
        as: "div",
        children: /*#__PURE__*/_jsxs(Small, {
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-50",
            display: "block",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-common.groupTags.tierHub.${tierHub}`
            })
          }), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: sortedTierGroups[tierHub].filter(tierLevel => isValidI18nKey(`marketplace-ui-common.groupTags.tierLevel.${tierLevel}`)).map(tierLevel => /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-common.groupTags.tierLevel.${tierLevel}`
            }, tierLevel))
          })]
        })
      }, tierHub)),
      footer: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(Link, {
          external: true,
          href: pricingPageUrl,
          onClick: () => onTrackInteraction({
            action: 'click tiers tooltip pricing link'
          }),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.plans.compatibilityPopover.seeAllPlans"
          })
        })
      })
    };
  }, [onTrackInteraction, pricingPageUrl, sortedTierGroups]);
  if (appContext === 'public') {
    return hasFeatureLevelScopeInfo ?
    /*#__PURE__*/
    // If we have feature level scope info, we want to show CompatibilityModal
    _jsx(Button, {
      use: "link",
      onClick: () => onCompatibilityModalOpen(),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.plans.compatibilityPopover.button"
      })
    }) :
    /*#__PURE__*/
    // If we don't have feature level scope info, fallback to details.tiers info
    _jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(List, {
        "data-test-id": "public-plan-compatibility-message",
        children: renderedTierGroups
      }), /*#__PURE__*/_jsx(Link, {
        "aria-label": unescapedText('detailsPage.compatibility.plans.ariaLabel'),
        external: true,
        href: pricingPageUrl,
        onClick: () => onTrackInteraction({
          action: 'click public pricing link'
        }),
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'detailsPage.compatibility.plans.learnMore'
          })
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(MediaObject, {
      align: "center",
      itemLeft: /*#__PURE__*/_jsx(CompatibilityIcon, {
        compatibilityType: compatibilityType
      }),
      spacing: "extra-small",
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "100",
        "data-test-id": "plan-compatibility-message",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.compatibility.plans.compatibilityDescription.${compatibilityType}`
        }), helpText]
      })
    }), hasFeatureLevelScopeInfo ?
    /*#__PURE__*/
    // If we have feature level scope info, we want to show CompatibilityModal
    _jsx(Button, {
      use: "link",
      onClick: () => onCompatibilityModalOpen(),
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.plans.compatibilityPopover.button"
        })
      })
    }) :
    /*#__PURE__*/
    // If we don't have feature level scope info, fallback to details.tiers info
    _jsx(Popover, {
      content: popoverContent,
      open: popoverOpen,
      onOpenChange: evt => setPopoverOpen(evt.target.value),
      showCloseButton: true,
      inlineSize: "250px",
      children: /*#__PURE__*/_jsx(Button, {
        onClick: () => {
          setPopoverOpen(true);
          onTrackInteraction({
            action: 'click see all compatible plans popover'
          });
        },
        use: "link",
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.plans.compatibilityPopover.button"
          })
        })
      })
    })]
  });
}